/* eslint-disable import/order */
import { differenceInSeconds, differenceInMilliseconds } from 'date-fns';
import { GlowFilter } from 'pixi-filters';

import { createSteppedGradient, randomNormal } from './utils';

import FirePack from '../img/fire.json';
import FireSprites from '../img/fire.png';
import ExplPack from '../img/mc.json';
import ExplSprites from '../img/mc.png';
import woodBackSprite from '../img/pixi/bois-back.png';
import woodFrontSprite from '../img/pixi/bois-front.png';
import fireSprite from '../img/pixi/fire.png';
import particleSprite from '../img/pixi/particle.png';
import {
    Particle,
    ParticleContainer,
    Application,
    Assets,
    Rectangle,
    Sprite,
    AnimatedSprite,
    Spritesheet,
} from 'pixi.js';

class Fire {
    container = null;

    config = {};

    items = [];

    fireSprites = [];

    fireFilters = [];

    tick = 0;

    scaleTick = 0;

    offsets = [-0.05, 0.075, -0.075, 0.05, 0.005];

    colors = [0xff0000, 0xff3300, 0xff6600, 0xff9900, 0xffcc00];

    explode = false;

    explosions = [];

    animateReveal = false;

    constructor(containerElement = null, config = null, items = null) {
        this.container = containerElement;
        this.config = config;
        this.items = items;
        this.app = null;
    }

    async init() {
        // Create a new application
        const app = new Application();
        const { items, container } = this;

        // Initialize the application
        await app.init({
            backgroundAlpha: 0,
            resizeTo: container,
            sharedTicker: true,
            antialias: true,
            useBackBuffer: true,
        });

        // Append the application canvas to the document body
        container.appendChild(app.canvas);

        this.app = app;

        const { width, height } = app.screen;
        const squareSide = width > height ? width / 2 : height / 2;
        const offsetY = squareSide / 10;
        const centerWidth = squareSide;
        const centerHeight = squareSide;
        const explosionsCount = width >= 768 ? 15 : 5;

        // Load the textures
        const fireTexture = await Assets.load(fireSprite);
        const particleTexture = await Assets.load(particleSprite);
        const woodFrontTexture = await Assets.load(woodFrontSprite);
        const woodBackTexture = await Assets.load(woodBackSprite);

        const backParticleContainer = new ParticleContainer({
            // boundsArea: new Rectangle(0, 0, width, height),
            dynamicProperties: {
                position: true, // Allow dynamic position changes (default)
                scale: false, // Static scale for extra performance
                rotation: false, // Static rotation
                color: true, // Static color
            },
        });

        app.stage.addChild(backParticleContainer);

        const particles = [];
        const backSpriteCount = 300;

        for (let i = 0; i < backSpriteCount; i += 1) {
            const isFire = Math.random() > 0.8;
            const item = new Particle(isFire ? fireTexture : particleTexture);

            if (isFire) {
                item.scale = 0.5;
            } else {
                item.scale = 0.1;
            }

            item.anchorX = 0.5;
            item.anchorY = 0.5;

            item.x = randomNormal() * (width / 2) + width / 4;
            item.y = height / 2;

            item.direction = 0.5 * Math.PI * 2;
            item.turningSpeed = Math.random() - 0.8;
            item.speed = 1 + Math.random();
            item.offset = Math.random() * 100;

            particles.push(item);

            backParticleContainer.addParticle(item);
        }

        // Append the wood
        const wood = new Sprite(woodBackTexture);
        wood.anchor.set(0.5);
        wood.x = width / 2;
        wood.y = height / 2 - offsetY;
        wood.width = centerWidth;
        wood.height = centerHeight;
        wood.alpha = 1;
        app.stage.addChild(wood);

        const FireSheet = await Assets.load(FireSprites);
        const FireSpriteSheet = new Spritesheet(FireSheet, FirePack);
        await FireSpriteSheet.parse();
        const fireTextures = Object.keys(FireSpriteSheet.textures).map(
            (key) => FireSpriteSheet.textures[key],
        );

        const animatedFire = new AnimatedSprite(fireTextures);
        animatedFire.anchor.set(0.5);
        animatedFire.x = width / 2;
        animatedFire.y = height / 2 - offsetY;
        animatedFire.width = centerWidth;
        animatedFire.height = centerHeight;
        animatedFire.rotation = 0;
        animatedFire.alpha = 1;
        animatedFire.animationSpeed = 0.1;

        app.stage.addChild(animatedFire);
        animatedFire.gotoAndPlay(0);

        // Append the images
        this.fireSprites = [...Array(5)].map(() => {
            const sprite = new Sprite();
            sprite.anchor.set(0.5);
            sprite.x = width / 2;
            sprite.y = height / 2;
            sprite.width = centerWidth / 2;
            // sprite.height = centerHeight / 2;
            sprite.alpha = 1;
            // sprite.blendMode = 'luminosity';
            app.stage.addChild(sprite);
            return sprite;
        });

        const aliases = items
            .map((item) => {
                const { label = null, image = null } = item || {};
                if (image === null || label === null) {
                    return null;
                }
                Assets.add({ alias: label, src: image });
                return label;
            })
            .filter((it) => it !== null);

        const textures = await Assets.load(aliases);

        this.fireFilters = this.fireSprites.map(
            () =>
                new GlowFilter({
                    color: 0xff6600,
                    distance: 20,
                    innerStrength: 1,
                }),
        );

        this.fireSprites.forEach((sprite, i) => {
            const item = items[i];
            const { label = null } = item || {};
            const texture = textures[label];
            const { width: itemWidth = null, height: itemHeight = null } = item || {};
            const ratio =
                itemWidth !== null && itemHeight !== null && itemWidth > 0 && itemHeight > 0
                    ? itemHeight / itemWidth
                    : 1;
            sprite.isSelf = false;
            sprite.sizeRatio = ratio;

            if (typeof texture !== 'undefined') {
                sprite.texture = texture;
            }
            if (typeof item !== 'undefined') {
                sprite.created_at = item.created_at;
            }
            sprite.filters = [this.fireFilters[i]];
            sprite.offsetX = this.offsets[i] * width;
        });

        const frontParticleContainer = new ParticleContainer({
            dynamicProperties: {
                position: true, // Allow dynamic position changes (default)
                scale: false, // Static scale for extra performance
                rotation: false, // Static rotation
                color: true, // Static color
            },
        });

        app.stage.addChild(frontParticleContainer);

        const frontParticles = [];
        const frontSpriteCount = 100;
        for (let i = 0; i < frontSpriteCount; i += 1) {
            const item = new Particle(Math.random() > 0.9 ? fireTexture : particleTexture);

            item.anchorX = 0.5;
            item.anchorY = 0.5;

            item.x = randomNormal() * (width / 2) + width / 4;
            item.y = height / 2;

            item.direction = 0.5 * Math.PI * 2;
            item.turningSpeed = Math.random() - 0.8;
            item.speed = 2 + Math.random() * 2;
            item.offset = Math.random() * 100;

            frontParticles.push(item);
            frontParticleContainer.addParticle(item);
        }

        // Append the wood
        const woodFront = new Sprite(woodFrontTexture);
        woodFront.anchor.set(0.5);
        woodFront.x = width / 2;
        woodFront.y = height / 2 - offsetY;
        woodFront.width = centerWidth;
        woodFront.height = centerHeight;
        woodFront.alpha = 1;
        app.stage.addChild(woodFront);

        // Add explosions
        const ExplSheet = await Assets.load(ExplSprites);
        const ExplSpriteSheet = new Spritesheet(ExplSheet, ExplPack);
        await ExplSpriteSheet.parse();
        const explosionTextures = Object.keys(ExplSpriteSheet.textures).map(
            (key) => ExplSpriteSheet.textures[key],
        );

        // Create and randomly place the animated explosion sprites on the stage
        for (let i = 0; i < explosionsCount; i += 1) {
            // Create an explosion AnimatedSprite
            const explosion = new AnimatedSprite(explosionTextures);
            explosion.alpha = 0;

            explosion.x = (Math.random() * app.screen.width) / 2 + app.screen.width / 4;
            explosion.y = (Math.random() * app.screen.height) / 2 + app.screen.height / 4;
            explosion.anchor.set(0.5);
            explosion.rotation = Math.random() * Math.PI;
            explosion.scale.set(0.75 + Math.random() * 0.5);

            this.explosions.push(explosion);
            app.stage.addChild(explosion);
        }

        const itemBounds = new Rectangle(
            (width - centerWidth) / 2,
            (height - centerHeight) / 2,
            centerWidth,
            centerHeight - offsetY,
        );

        const parts = createSteppedGradient(
            [
                { value: '0x0000ff', time: 0 },
                { value: '0x0000ff', time: 0.05 },
                { value: '0x0099ff', time: 0.7 },
                { value: '0x00ffff', time: 1 },
            ],
            20,
        );

        this.tick = 0;
        app.ticker.add(() => {
            const now = new Date();
            let factor = 1;
            const animatedDiff = this.scaleTick - this.tick;

            for (let i = 0; i < this.items.length; i += 1) {
                const item = this.items[i];
                if (typeof item === 'undefined') {
                    // console.log('what');
                    // eslint-disable-next-line no-continue
                    continue;
                }

                const seconds = Math.abs(differenceInSeconds(now, item?.created_at));
                const ratio = seconds < 30 ? 1 - seconds / 30 : 0;
                const offsetId = parseInt(item.id, 10) % 5;
                const offsetX = this.offsets[offsetId] * width;

                this.fireSprites[i].alpha = ratio;
                factor += ratio;
                const diff = 100 - animatedDiff;
                const nextX = width / 2 + offsetX;
                const nextY = height / 2;
                const nextWidth = Math.floor(centerWidth / 2);
                const nextHeight = Math.floor(nextWidth * (this.fireSprites[i].sizeRatio || 1));

                if (i === 4 && this.animateReveal && diff < 1) {
                    this.fireSprites[i].width = nextWidth * diff;
                    this.fireSprites[i].height = nextHeight * diff;
                    if (this.fireSprites[i].isSelf) {
                        if (width <= 768) {
                            this.fireSprites[i].x = nextX;
                            this.fireSprites[i].y = nextY + nextY * (1 - diff);
                        } else {
                            this.fireSprites[i].x = nextX * diff;
                            this.fireSprites[i].y = nextY;
                        }
                    } else {
                        if (width <= 768) {
                            this.fireSprites[i].x = nextX;
                            this.fireSprites[i].y = nextY * diff;
                        } else {
                            this.fireSprites[i].x = nextX + nextX * (1 - diff);
                            this.fireSprites[i].y = nextY;
                        }
                    }
                } else {
                    this.fireSprites[i].width = nextWidth;
                    this.fireSprites[i].height = nextHeight;
                    this.fireSprites[i].x = nextX;
                    this.fireSprites[i].y = nextY;
                }
            }

            for (let i = 0; i < this.fireFilters.length; i += 1) {
                const item = this.items[i];
                if (typeof item !== 'undefined') {
                    const ms = differenceInMilliseconds(now, item.created_at);
                    const filter = this.fireFilters[i];
                    filter.innerStrength = ms / 10000;
                }
            }

            this.intensity = factor;
            const drop = Math.min(1 - factor / 6, 0.2);

            for (let i = 0; i < particles.length; i += 1) {
                const item = particles[i];

                item.y += Math.cos(item.direction) * (item.speed * item.scaleY * factor);
                item.rotation = -item.direction + Math.PI;

                item.x += i % 2 === 0 ? Math.sin(this.tick) * 0.3 : Math.sin(this.tick) * -1 * 0.3;

                if (item.y < itemBounds.y) {
                    item.y += itemBounds.height - offsetY * 1.5;
                }

                const ratio = item.y / height;
                const alphaRatio = ratio - drop;

                const index = Math.floor(ratio * 18);
                const part = parts[index];
                item.tint = typeof part !== 'undefined' ? part.value : 0x0000ff;

                if (item.y > height / 1.5) {
                    item.alpha = alphaRatio - 0.2;
                } else if (item.y > height / 3) {
                    item.alpha = alphaRatio - 0.1;
                } else if (item.y > height / 5) {
                    item.alpha = alphaRatio - 0.1;
                } else {
                    item.alpha = alphaRatio;
                }

                if (item.y > height / 2 + offsetY) {
                    item.alpha = 0;
                }
            }

            for (let i = 0; i < frontParticles.length; i += 1) {
                const item = frontParticles[i];

                item.y += Math.cos(item.direction) * (item.speed * item.scaleY);
                item.x += i % 2 === 0 ? Math.sin(this.tick) * 0.5 : Math.sin(this.tick) * -1 * 0.5;

                if (item.y < itemBounds.y) {
                    item.y += itemBounds.height - offsetY * 1.5;
                }

                const ratio = item.y / (height - offsetY);
                const alphaRatio = ratio - drop;

                const index = Math.floor(ratio * 18);
                const part = parts[index];
                item.tint = typeof part !== 'undefined' ? part.value : 0x0000ff;

                if (item.y > height / 1.5) {
                    item.alpha = alphaRatio - 0.2;
                } else if (item.y > height / 3) {
                    item.alpha = alphaRatio - 0.1;
                } else if (item.y > height / 5) {
                    item.alpha = alphaRatio - 0.1;
                } else {
                    item.alpha = alphaRatio - 0.3;
                }
                if (item.y > height / 2 + offsetY) {
                    item.alpha = 0;
                }
            }

            // Increment the ticker
            this.tick += 0.01;
        });

        return app;
    }

    async update(config, items, user) {
        if (this.app === null) {
            return;
        }

        // Kill loop when updating textures...
        this.app.ticker.stop();

        this.config = config;
        this.items = items.slice(items.length - 5);

        const aliases = this.items
            .map((item) => {
                const { label = null, image = null } = item || {};
                if (image === null || label === null) {
                    return null;
                }
                Assets.add({ alias: label, src: image });
                return label;
            })
            .filter((it) => it !== null);

        const textures = await Assets.load(aliases);

        this.fireSprites.forEach((sprite, i) => {
            if (typeof this.items[i] !== 'undefined') {
                const item = this.items[i];
                const {
                    label = null,
                    width = null,
                    height = null,
                    user: itemUser = null,
                } = item || {};

                const texture = textures[label];
                if (typeof texture !== 'undefined') {
                    const ratio =
                        width !== null && height !== null && width > 0 && height > 0
                            ? height / width
                            : 1;
                    sprite.created_at = item.created_at;
                    sprite.sizeRatio = ratio;
                    sprite.isSelf = user === itemUser;
                    sprite.texture = texture;
                } else {
                    sprite.created_at = null;
                    sprite.sizeRatio = 1;
                    sprite.isSelf = false;
                    sprite.texture = null;
                }
            } else {
                sprite.texture = null;
                sprite.created_at = null;
            }
        });

        this.app.ticker.start();
    }

    makeExplosions(duration = 1000) {
        if (this.explosions !== null) {
            this.explosions.forEach((explosion) => {
                explosion.alpha = 1;
                explosion.gotoAndPlay((Math.random() * 26) | 0);
            });
            clearTimeout(this.explosionsId);
            this.explosionsId = setTimeout(() => {
                this.explosions.forEach((explosion) => {
                    explosion.alpha = 0;
                    explosion.stop();
                });
            }, duration);
        }
    }

    makeAnimatedReveal(duration = 1000) {
        this.animateReveal = true;
        this.scaleTick = this.tick + 100;
        clearTimeout(this.revealId);
        this.revealId = setTimeout(() => {
            this.animateReveal = false;
        }, duration * 2);
    }
}

export default Fire;
